import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { SITE_NAME } from '@/config/site';
import { useAuthStore } from '@/stores/auth-store';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useDippyOnboarding } from '@/hooks/useDippyOnboarding';
import { cn } from '@/lib/utils';
import {
  adminNavSections,
  isRouteActive,
  getActiveSectionKey,
  getActiveGroupKey,
  getOrderedSections,
  navItemVisible,
  type AdminNavSection,
  type AdminNavGroup,
  type AdminNavItem,
  type BadgeColor,
} from '@/config/admin-nav';
import { useNavBadges, useNavContent, type NavBadges } from '@/hooks/useNavBadges';
import { Menu, X, ChevronLeft, ChevronRight, Store, HelpCircle } from 'lucide-react';
import { SITE_SHORT_NAME, SITE_TYPE } from '@/config/site';
import NotificationBell from '@/components/admin/NotificationBell';
import Dippy from '@/components/admin/dippy/Dippy';
import { useAxissWidget } from '@/hooks/useAxissWidget';
import { openAxissAdmin } from '@/lib/axiss-sso';
import { isDesignerPath, DESIGNER_HOME } from '@/config/designer-surface';
import { canAccessAdminShell, isDesigner } from '@/lib/roles';

const STORAGE_KEY = 'admin-nav-open-sections';
// Sub-groups default to OPEN (so expanding a section immediately shows its
// grouped items); we persist the ones the admin has explicitly collapsed.
const STORAGE_KEY_GROUPS = 'admin-nav-collapsed-groups';

function loadOpenSections(): Set<string> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch { /* ignore */ }
  return new Set<string>();
}

function saveOpenSections(sections: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...sections]));
}

function loadCollapsedGroups(): Set<string> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_GROUPS);
    if (saved) return new Set(JSON.parse(saved));
  } catch { /* ignore */ }
  return new Set<string>();
}

function saveCollapsedGroups(keys: Set<string>) {
  localStorage.setItem(STORAGE_KEY_GROUPS, JSON.stringify([...keys]));
}

export default function AdminLayout() {
  const { user, isInitialized } = useAuthStore();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isEnabled } = useFeatureFlags();
  const badges = useNavBadges();
  const navContent = useNavContent();
  useAxissWidget();

  // Admin pages don't render DocumentHead, so without this the tab keeps the
  // static index.html title for the whole session (which on stale tenant
  // builds was a mangled duplicate like "… Seed Co. Seed Co.").
  useEffect(() => {
    document.title = `Admin · ${SITE_NAME}`;
    return () => {
      document.title = SITE_NAME;
    };
  }, []);

  // Initialize open sections from localStorage + auto-expand active section
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const saved = loadOpenSections();
    const activeKey = getActiveSectionKey(location.pathname);
    if (activeKey) saved.add(activeKey);
    return saved;
  });

  const toggleSection = useCallback((key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      saveOpenSections(next);
      return next;
    });
  }, []);

  // Sub-group collapse state (default open). The active route's group is always
  // forced open regardless of saved state (see SidebarSection) so navigating
  // into a collapsed group still reveals the current page.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(loadCollapsedGroups);
  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      saveCollapsedGroups(next);
      return next;
    });
  }, []);
  const activeGroupKey = useMemo(
    () => getActiveGroupKey(location.pathname),
    [location.pathname]
  );

  // Order sections (primary domain floats to top for this site type), then
  // filter sections, sub-groups, and items by feature flags / site type /
  // super-admin access. Empty groups and empty sections are dropped.
  const isPlatformAdmin = user?.isPlatformAdmin ?? false;
  const designerOnly = isDesigner(user?.role);
  const visibleSections = useMemo(() => {
    const itemVisible = (i: AdminNavItem) =>
      // A designer sees exactly the pages they can open. Derived from the same
      // DESIGNER_PATH_PREFIXES list the route guard below uses, rather than a
      // per-item flag, so the sidebar and the router can never disagree — a nav
      // entry that survives this filter is by construction a page that will
      // actually render. Sections left with no items are dropped downstream.
      (!designerOnly || isDesignerPath(i.href))
      && (!i.designerOnly || designerOnly)
      && navItemVisible(i, SITE_TYPE, isEnabled)
      && (!i.superAdmin || isPlatformAdmin)
      // Hide pages the tenant has no content for (Audiences, Funnels, Brand
      // Cast, Video Studio are empty almost everywhere). Defaults to visible
      // until the counts load, so nothing vanishes under the cursor — and the
      // page you are CURRENTLY on is never hidden, or you'd be stranded on a
      // route with no way back to it.
      && (!i.hideWhenEmptyKey
        || navContent[i.hideWhenEmptyKey]
        || isRouteActive(location.pathname, i.href));
    return getOrderedSections(adminNavSections, SITE_TYPE)
      .filter((s) => navItemVisible(s, SITE_TYPE, isEnabled))
      .filter((s) => !s.superAdmin || isPlatformAdmin)
      .map((s) => ({
        ...s,
        items: s.items.filter(itemVisible),
        groups: (s.groups ?? [])
          .filter((g) => navItemVisible(g, SITE_TYPE, isEnabled))
          .filter((g) => !g.superAdmin || isPlatformAdmin)
          .map((g) => ({ ...g, items: g.items.filter(itemVisible) }))
          .filter((g) => g.items.length > 0),
      }))
      .filter((s) => s.items.length > 0 || s.groups.length > 0);
  }, [isEnabled, isPlatformAdmin, designerOnly, navContent, location.pathname]);

  // First-run welcome wizard gate. New sites get bounced to /admin/welcome
  // on their first admin visit so the admin picks an autonomy preset,
  // confirms brand copy, and launches Dippy's first real task instead of
  // landing on a blank revenue dashboard with no prompting.
  const dippyOnboarding = useDippyOnboarding();
  const isOnWelcomeRoute = location.pathname.startsWith('/admin/welcome');

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || !canAccessAdminShell(user.role)) {
    return <Navigate to="/" replace />;
  }

  // One guard in front of every admin route, allowing only what is listed —
  // so a route added tomorrow is denied by default rather than exposed by
  // default. This runs before <Outlet/>, so a denied deep link never renders
  // the page, and the API would 403 it anyway. UX, not the security boundary:
  // functions/src/middleware/admin.ts is.
  if (designerOnly && !isDesignerPath(location.pathname)) {
    return <Navigate to={DESIGNER_HOME} replace />;
  }

  // Wait for the onboarding check before rendering the layout — prevents
  // a flash of the revenue dashboard for first-time admins.
  if (!dippyOnboarding.isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!dippyOnboarding.isComplete && !isOnWelcomeRoute) {
    return <Navigate to="/admin/welcome" replace />;
  }

  return (
    <div className="min-h-screen flex bg-stone-100">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-white border-r border-stone-200 flex flex-col overflow-y-auto transition-transform duration-200',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        aria-label="Admin navigation"
      >
        <div className="flex items-center justify-between h-14 px-4 border-b border-stone-200 shrink-0">
          <Link to="/admin" className="flex items-center gap-2">
            <Store className="h-5 w-5 text-sage-600" />
            <span className="font-display font-bold text-sage-800">{SITE_SHORT_NAME} Admin</span>
          </Link>
          <button
            className="lg:hidden p-1 text-stone-500 hover:text-stone-700"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {visibleSections.map((section) => (
            <SidebarSection
              key={section.key}
              section={section}
              isOpen={section.collapsible === false || openSections.has(section.key)}
              onToggle={() => toggleSection(section.key)}
              pathname={location.pathname}
              onNavClick={() => setSidebarOpen(false)}
              badges={badges}
              collapsedGroups={collapsedGroups}
              onToggleGroup={toggleGroup}
              activeGroupKey={activeGroupKey}
            />
          ))}
        </nav>

        <div className="p-3 border-t border-stone-200 shrink-0">
          <button
            onClick={() => void openAxissAdmin()}
            className="flex items-center gap-2 px-3 py-2 text-sm text-stone-500 hover:text-stone-700 rounded-md hover:bg-stone-50 w-full"
          >
            <HelpCircle className="h-4 w-4" />
            Manage Support
            <span className="ml-auto flex items-center gap-1">
              {badges.support_tickets > 0 && (
                <NavBadgePill count={badges.support_tickets} color="red" />
              )}
              {badges.new_bugs > 0 && (
                <NavBadgePill count={badges.new_bugs} color="amber" />
              )}
            </span>
          </button>
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 text-sm text-stone-500 hover:text-stone-700 rounded-md hover:bg-stone-50"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Store
          </Link>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 bg-white border-b border-stone-200 flex items-center px-4 lg:px-6 gap-4">
          <button
            className="lg:hidden p-2 -ml-2 text-stone-600 hover:text-stone-900"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <NotificationBell />
          <div className="text-sm text-stone-500">
            {user?.firstName || user?.email}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Dippy assistant */}
      <Dippy />
    </div>
  );
}

const badgeColorClasses: Record<BadgeColor, string> = {
  red: 'bg-red-500 text-white',
  amber: 'bg-amber-500 text-white',
};

function NavBadgePill({ count, color }: { count: number; color: BadgeColor }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none',
        badgeColorClasses[color]
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarNavItem({
  item,
  pathname,
  onNavClick,
  badges,
  indent = false,
}: {
  item: AdminNavItem;
  pathname: string;
  onNavClick: () => void;
  badges: NavBadges;
  indent?: boolean;
}) {
  const Icon = item.icon;
  const base = cn(
    'flex items-center gap-2.5 py-2 rounded-md text-sm font-medium transition-colors',
    indent ? 'pl-8 pr-3' : 'px-3'
  );

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(base, 'text-stone-600 hover:bg-stone-50 hover:text-stone-900')}
        onClick={onNavClick}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {item.label}
      </a>
    );
  }

  const active = isRouteActive(pathname, item.href);
  const badgeCount = item.badgeKey ? (badges[item.badgeKey as keyof NavBadges] ?? 0) : 0;
  return (
    <Link
      to={item.href}
      className={cn(
        base,
        active
          ? 'bg-sage-50 text-sage-800'
          : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
      )}
      onClick={onNavClick}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {item.label}
      {badgeCount > 0 && item.badgeColor && (
        <span className="ml-auto">
          <NavBadgePill count={badgeCount} color={item.badgeColor} />
        </span>
      )}
    </Link>
  );
}

function SidebarGroup({
  group,
  isOpen,
  onToggle,
  pathname,
  onNavClick,
  badges,
}: {
  group: AdminNavGroup;
  isOpen: boolean;
  onToggle: () => void;
  pathname: string;
  onNavClick: () => void;
  badges: NavBadges;
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full pl-5 pr-3 py-1.5 text-[10px] font-semibold text-stone-400 tracking-wider hover:text-stone-600 transition-colors cursor-pointer"
        aria-expanded={isOpen}
      >
        {group.label}
        <ChevronRight
          className={cn(
            'h-3 w-3 transition-transform duration-150',
            isOpen && 'rotate-90'
          )}
        />
      </button>
      {isOpen && (
        <div className="space-y-0.5">
          {group.items.map((item) => (
            <SidebarNavItem
              key={item.href}
              item={item}
              pathname={pathname}
              onNavClick={onNavClick}
              badges={badges}
              indent
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarSection({
  section,
  isOpen,
  onToggle,
  pathname,
  onNavClick,
  badges,
  collapsedGroups,
  onToggleGroup,
  activeGroupKey,
}: {
  section: AdminNavSection;
  isOpen: boolean;
  onToggle: () => void;
  pathname: string;
  onNavClick: () => void;
  badges: NavBadges;
  collapsedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  activeGroupKey: string | null;
}) {
  const collapsible = section.collapsible !== false;

  return (
    <div>
      {collapsible ? (
        <button
          onClick={onToggle}
          className="flex items-center justify-between w-full px-3 py-1.5 text-[10px] font-semibold text-stone-400 tracking-wider hover:text-stone-600 transition-colors cursor-pointer"
          aria-expanded={isOpen}
        >
          {section.label}
          <ChevronRight
            className={cn(
              'h-3 w-3 transition-transform duration-150',
              isOpen && 'rotate-90'
            )}
          />
        </button>
      ) : (
        <p className="px-3 py-1.5 text-[10px] font-semibold text-stone-400 tracking-wider">
          {section.label}
        </p>
      )}

      {isOpen && (
        <div className="space-y-0.5">
          {section.items.map((item) => (
            <SidebarNavItem
              key={item.href}
              item={item}
              pathname={pathname}
              onNavClick={onNavClick}
              badges={badges}
            />
          ))}
          {section.groups?.map((group) => {
            const groupKey = `${section.key}:${group.key}`;
            // Default open; the active route's group is always forced open.
            const groupOpen = activeGroupKey === groupKey || !collapsedGroups.has(groupKey);
            return (
              <SidebarGroup
                key={group.key}
                group={group}
                isOpen={groupOpen}
                onToggle={() => onToggleGroup(groupKey)}
                pathname={pathname}
                onNavClick={onNavClick}
                badges={badges}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
